import _ from "lodash";

/**
 * Returns true if the version has no components and no eventBindings,
 * meaning it has never been configured through the PlayEngine.
 *
 * @param {Object} version — the selectedVersion object
 * @returns {boolean}
 */
export function isVersionEmpty(version) {
  const components = _.get(version, "playEngineConfig.components", []);
  const eventBindings = _.get(version, "playEngineConfig.eventBindings", []);
  return Array.isArray(components) && components.length === 0
    && Array.isArray(eventBindings) && eventBindings.length === 0;
}

/**
 * Returns true if the version has an applied preset (_presetId is set and non-empty).
 *
 * @param {Object} version — the selectedVersion object
 * @returns {boolean}
 */
export function versionHasPreset(version) {
  const presetId = _.get(version, "playEngineConfig._presetId", "");
  return Boolean(presetId);
}

/**
 * Deep-clones a playEngineConfig object and strips all asset media data,
 * leaving dependency slot shapes and asset-map keys intact so the preset acts
 * as a template. For the asset-map format this keeps `assets` keys + each
 * dependency's `assetKey`, but nulls the underlying media `source`/`sourceRef`.
 * Also removes the _presetId marker since the result is a new preset template.
 *
 * @param {Object} playEngineConfig
 * @returns {Object} stripped clone — safe to save as a preset
 */
export function stripAssetDependencies(playEngineConfig) {
  const config = _.cloneDeep(playEngineConfig || {});

  // Remove the _presetId marker — the preset itself should not carry it
  delete config._presetId;

  // Asset-map: keep the keys (slot wiring) but null the media sources.
  if (config.assets && !Array.isArray(config.assets) && typeof config.assets === "object") {
    for (const entry of Object.values(config.assets)) {
      if (entry && typeof entry === "object") {
        entry.source = null;
        entry.sourceRef = null;
      }
    }
  }

  // Strip component dependencies (dep-centric bytes); keep assetKey pointers.
  const components = config.components || [];
  for (const comp of components) {
    for (const dep of (comp.dependencies || [])) {
      dep.source = null;
      dep.sourceRef = null;
      delete dep.assetId;
      delete dep.isSatisfied;
    }
  }

  // Strip localization override dependencies. Asset-map override values are
  // asset key strings (kept); dep-centric enriched objects get sources nulled.
  const overrides = _.get(config, "localization.overrides", {});
  for (const lang of Object.values(overrides)) {
    for (const compOverride of Object.values(lang)) {
      for (const dep of Object.values(compOverride.dependencyOverrides || {})) {
        if (dep && typeof dep === "object") {
          dep.source = null;
          dep.sourceRef = null;
          delete dep.assetId;
          delete dep.isSatisfied;
        }
      }
    }
  }

  return config;
}
